import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Drawer, Table, Popover, Divider, Modal, Button, Spin } from 'antd';
import { isMobile } from 'react-device-detect';
import { isValidDate, renderPaymentMethods, renderPaymentStatus } from '@/utils/helpers';
import { DeleteOutlined, DownOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import NumberFormat from 'react-number-format';
import { checkPermission } from '@/utils/permission';
import { removeCustomerPayment, getSpecificCustomerPayment, getCustomerPaymentCustomer } from '@/redux/actions/CustomerPayment';
import CustomerPaymentForm from './CustomerPaymentForm';
import ExpandableText from '@/components/ExpandableText';

const { confirm } = Modal;

class CustomerPaymentList extends Component {

    constructor() {
        super();
        this.state = {
            isOpenForm: false,
            currentItem: null,

            loading: false,
            items: [], // dataSource nội bộ
        };
    }

    componentDidMount() {
        const { visible, customerId } = this.props;
        if (visible && customerId) this.loadData();
    }

    componentDidUpdate(prevProps) {
        const { visible, customerId } = this.props;

        const justOpened = !prevProps.visible && visible;
        const changedCustomer = prevProps.customerId !== customerId;

        if (visible && customerId && (justOpened || changedCustomer)) {
            this.loadData();
        }

        // đóng drawer -> reset form state (optional)
        if (prevProps.visible && !visible) {
            this.setState({ isOpenForm: false, currentItem: null });
        }
    }

    loadData = async () => {
        const { customerId } = this.props;
        if (!customerId) return;

        this.setState({ loading: true });

        try {
            const items = await this.props.getCustomerPaymentCustomer(customerId);
            this.setState({ items: items || [], loading: false });
        } catch (e) {
            this.setState({ loading: false });
        }
    };

    toggleOpenForm(isOpenForm, currentItem = null) {
        this.setState({ isOpenForm, currentItem });
    }

    onEdit(id) {
        this.props.getSpecificCustomerPayment(id).then((res) => {
            this.toggleOpenForm(true, res);
        });
    }

    onRemove(id) {
        confirm({
            title: 'Cảnh báo',
            content: 'Bạn chắc chắn muốn xoá bản ghi này?',
            okText: 'OK',
            cancelText: 'Huỷ',
            onOk: async () => {
                await this.props.removeCustomerPayment(id);
                await this.loadData(); // tự reload
                setTimeout(() => Modal.destroyAll(), 300);
            },
        });
    }

    onClose() {
        this.props.onCancel();
    }

    render() {
        const { isOpenForm, currentItem, loading, items } = this.state;
        const { visible, customerId } = this.props;

        const columns = [
            {
                title: '#',
                width: '80px',
                align: 'center',
                render: (text, record) => (
                    <div>
                        {checkPermission('customer_payment', 'update') ? (
                            <>
                                <EditOutlined
                                    className='color-primary'
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => this.onEdit(record.id)}
                                />
                                <Divider type='vertical' />
                            </>
                        ) : null}

                        {checkPermission('customer_payment', 'remove') ? (
                            <DeleteOutlined
                                style={{ color: 'red', cursor: 'pointer' }}
                                onClick={() => this.onRemove(record.id)} // truyền 1 id thôi
                            />
                        ) : null}
                    </div>
                ),
            },
            {
                title: 'Hình thức',
                dataIndex: 'payment_method',
                key: 'payment_method',
                width: '140px',
                render: (_, record) => <div>{renderPaymentMethods(record.payment_method)}</div>,
            },
            {
                title: 'Ngày bắt đầu',
                dataIndex: 'start_at',
                key: 'start_at',
                width: '120px',
                render: (text) => <>{isValidDate(text, false)}</>,
            },
            {
                title: 'Dịch vụ áp dụng',
                dataIndex: 'payment_services',
                key: 'payment_services',
                render: (_, record) => {
                    const services = record?.payment_services;
                    if (!services?.length) return null;

                    const content = services.map((item, index) => (
                        <div key={index} style={{ padding: '5px 0' }}>
                            {item.customer_service?.service?.name}
                        </div>
                    ));

                    return (
                        <div className='item-action-btn'>
                            <Popover placement="right" content={content}>
                                {services[0]?.customer_service?.service?.name} <DownOutlined />
                            </Popover>
                        </div>
                    );
                },
            },
            {
                title: 'Tổng số tiền',
                dataIndex: 'total_amount',
                key: 'total_amount',
                width: '120px',
                render: (text) => (
                    <NumberFormat value={text} displayType={'text'} suffix="đ" thousandSeparator />
                ),
            },
            {
                title: 'Số tháng',
                dataIndex: 'installment_months',
                key: 'installment_months',
                width: '80px',
            },
            {
                title: 'Trả góp/tháng',
                dataIndex: 'monthly_payment',
                key: 'monthly_payment',
                width: '120px',
                render: (text) => (
                    <NumberFormat value={text} displayType={'text'} suffix="đ" thousandSeparator />
                ),
            },
            {
                title: 'Trạng thái',
                dataIndex: 'status',
                key: 'status',
                width: '100px',
                render: (text) => <div>{renderPaymentStatus(text)}</div>,
            },
            {
                title: 'Ghi chú',
                dataIndex: 'note',
                key: 'note',
                width: '180px',
                render: (text) => <ExpandableText text={text} rows={1} />,
            },
            {
                title: 'Ngày tạo',
                dataIndex: 'created_at',
                key: 'created_at',
                width: '150px',
                render: (text) => <>{isValidDate(text, true)}</>,
            },
            {
                title: 'Người tạo',
                dataIndex: 'created_user',
                key: 'created_user',
                width: '200px',
                render: (_, record) => <>{record?.created_user?.full_name}</>,
            },
        ];

        return (
            <>
                <Drawer
                    visible={visible}
                    title="Hình thức thanh toán"
                    onClose={() => this.onClose()}
                    maskClosable={false}
                    width={isMobile ? '100%' : '75%'}
                >
                    {visible ? (
                        <Spin spinning={loading}>
                            <Button
                                type='primary'
                                icon={<PlusOutlined />}
                                onClick={() => this.toggleOpenForm(true)}
                                className='mb-2'
                                disabled={!checkPermission('customer_payment', 'add')}
                            >
                                Thêm mới
                            </Button>

                            <Table
                                size='small'
                                rowKey='id'              // nên dùng id thay vì key
                                tableLayout="auto"
                                columns={columns}
                                dataSource={items || []} // dùng state.items
                                pagination={false}
                                scroll={{ x: 'max-content' }}
                                className='mb-4 w-100'
                                locale={{ emptyText: "Chưa có dữ liệu" }}
                            />
                        </Spin>
                    ) : null}
                </Drawer>

                <CustomerPaymentForm
                    visible={isOpenForm}
                    customerId={customerId}
                    data={items} // truyền list hiện tại nếu form cần
                    onCancel={() => this.toggleOpenForm(false)}
                    currentItem={currentItem}
                    reloadData={() => this.loadData()} // form submit xong tự reload
                />
            </>
        );
    }
}

function mapDispatchToProps(dispatch) {
    return {
        removeCustomerPayment: (id) => dispatch(removeCustomerPayment(id)),
        getSpecificCustomerPayment: (id) => dispatch(getSpecificCustomerPayment(id)),
        getCustomerPaymentCustomer: (customerId) => dispatch(getCustomerPaymentCustomer(customerId)),
    };
}

export default connect(null, mapDispatchToProps)(CustomerPaymentList);